import { useEffect } from 'react';
import PropTypes from 'react-proptypes';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { currencyFormatter } from '@dnhb/formatters';
import { dispatchMapper, INIT, NAVIGATE, SYNC } from '../../../shared/wizard/actions';
import { shapeInput, shapeOutput } from '../config';
import CalculationValue from 'shared/legacy/CalculationValue';
import Analytics from 'shared/utility/analytics';

const WizardResults = ({ pages, input, output, forward, dispatch, sync, recalculating }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = location.state?.page;
  const currentPageIndex = pages.indexOf(currentPage);
  const pageIndex = pages.indexOf(location.state?.page);
  const { percentage_of_max_mortgage } = output;

  useEffect(() => {
    if (pages.length > 3 && currentPage !== 'final' && typeof recalculating !== 'number') {
      dispatch({ type: INIT, location, navigate, payload: { pages: pages.slice(0, 3) } });
    }
  }, [currentPage, dispatch, pages, recalculating]);

  useEffect(() => {
    dispatch({ type: SYNC, location, navigate, syncing: false });
    if (!percentage_of_max_mortgage) sync('hide_next_step_button', true);
    Analytics.send('Bereken', `Resultaten: [${pageIndex > 3 ? 'Premium' : 'Standaard'}]`);
    return () => sync('hide_next_step_button', false);
  }, [dispatch, percentage_of_max_mortgage, pageIndex, pages, sync]);

  return (
    <form onSubmit={forward(recalculating, location, navigate)}>
      <div className="wrapper assumptions">
        <h2>Uitgangspunten van deze berekening</h2>
        <div className="row">
          <div className="col-xs-12">
            <CalculationValue
              label="Uw geboortedatum"
              value={input.applicant_dob.value}
              svgClickHandler={() => {
                dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 0 });
              }}
              editable
            />
          </div>
        </div>
        {input.applicant_has_partner ? (
          <div className="row">
            <div className="col-xs-12">
              <CalculationValue
                label="Geboortedatum partner"
                value={input.partner_dob.value}
                svgClickHandler={() => {
                  dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 0 });
                }}
                editable
              />
            </div>
          </div>
        ) : null}
        <div className="row">
          <div className="col-xs-12">
            <CalculationValue
              label="Aflossingsvrije hypotheekschuld"
              value={currencyFormatter(Math.round(input.interest_only_mortgage_debt || 0))}
              svgClickHandler={() => {
                dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 1 });
              }}
              editable
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <CalculationValue
              label="Huidige woningwaarde"
              value={currencyFormatter(Math.round(input.home_value || 0))}
              svgClickHandler={() => {
                dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 1 });
              }}
              editable
            />
          </div>
        </div>
        <div key="mortgage_end_date" className="row">
          <div className="col-xs-12">
            <CalculationValue
              label="Einddatum hypotheek"
              value={input.mortgage_end_date.value}
              svgClickHandler={() => {
                dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 1 });
              }}
              editable
            />
          </div>
        </div>
        {currentPage !== 'results'
          ? [
            output.applicant_show_income ? (
              <div key="applicant_income" className="row">
                <div className="col-xs-12">
                  <CalculationValue
                    label={`Uw bruto inkomen per ${input.applicant_income_period === 'yearly' ? 'jaar' : 'maand'}`}
                    value={currencyFormatter(Math.round(input.applicant_income || 0))}
                    svgClickHandler={() => {
                      dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 3 });
                    }}
                    editable
                  />
                </div>
              </div>
            ) : null,
            output.applicant_show_aow ? (
              <div key="applicant_pension_income" className="row">
                <div className="col-xs-12">
                  <CalculationValue
                    label={`Uw pensioeninkomen per ${
                      input.applicant_pension_income_period === 'yearly' ? 'jaar' : 'maand'
                    }`}
                    value={currencyFormatter(Math.round(input.applicant_pension_income || 0))}
                    svgClickHandler={() => {
                      dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 3 });
                    }}
                    editable
                  />
                </div>
              </div>
            ) : null,
            output.partner_show_income ? (
              <div key="partner_income" className="row">
                <div className="col-xs-12">
                  <CalculationValue
                    label={`Bruto inkomen partner per ${input.partner_income_period === 'yearly' ? 'jaar' : 'maand'}`}
                    value={currencyFormatter(Math.round(input.partner_income || 0))}
                    svgClickHandler={() => {
                      dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 3 });
                    }}
                    editable
                  />
                </div>
              </div>
            ) : null,
            output.partner_show_aow ? (
              <div key="partner_pension_income" className="row">
                <div className="col-xs-12">
                  <CalculationValue
                    label={`Partner pensioeninkomen per ${
                      input.partner_pension_income_period === 'yearly' ? 'jaar' : 'maand'
                    }`}
                    value={currencyFormatter(Math.round(input.partner_pension_income || 0))}
                    svgClickHandler={() => {
                      dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 3 });
                    }}
                    editable
                  />
                </div>
              </div>
            ) : null,
            input.has_own_resources ? (
              <div key="own_resources" className="row">
                <div className="col-xs-12">
                  <CalculationValue
                    label="Eigen geld"
                    value={currencyFormatter(Math.round(input.own_resources || 0))}
                    svgClickHandler={() => {
                      dispatch({ type: NAVIGATE, location, navigate, recalculate: currentPageIndex, index: 4 });
                    }}
                    editable
                  />
                </div>
              </div>
            ) : null,
          ]
          : null}
      </div>
    </form>
  );
};

WizardResults.propTypes = {
  forward: PropTypes.func.isRequired,
  recalculating: PropTypes.number,
  dispatch: PropTypes.func.isRequired,
  sync: PropTypes.func.isRequired,
  pages: PropTypes.arrayOf(PropTypes.string).isRequired,
  input: shapeInput.isRequired,
  output: shapeOutput.isRequired,
};

WizardResults.defaultProps = {
  recalculating: null,
};

export default connect(
  state => ({
    recalculating: state.recalculating,
    syncing: state.syncing,
    pages: state?.pages,
    input: state.input,
    output: state.output,
    tab: state.tab,
  }),
  dispatchMapper
)(WizardResults);
